import * as React from 'react';
import MaterialAutocomplete, { AutocompleteProps as MaterialAutocompleteProps } from '@material-ui/lab/Autocomplete';

import { isString } from 'formik';
import { CustomDropdownProps, CustomDropdown, DropdownListItem, DefaultDropdownMenu } from '../../../Dropdown';
import { Loader } from '../../../Generic';
import { PuppetTextInputProps, PuppetTextInput } from '../../../Input';

export type DSAutocompleteProps<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> = MaterialAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>;

export const DSAutocomplete = MaterialAutocomplete;

export type AutocompleteProps<T> = Omit<
    PuppetTextInputProps,
    'onChange' | 'onClear' | 'onClick' | 'onSelect' | 'value'
> &
    Pick<CustomDropdownProps, 'menuStyle'> & {
        inputValue?: string;
        isLoadingOptions?: boolean;
        notFoundMessage?: JSX.Element | string;
        onClear?: () => void;
        onSearch: (term: string) => void;
        onSelect: (selected: T) => void;
        options: T[];
        renderDropdown?: (renderList: () => JSX.Element) => JSX.Element;
        renderListOption: (option: T) => string;
    };

export function Autocomplete<T>({
    inputValue: initialInputValue,
    isLoadingOptions = false,
    menuStyle,
    notFoundMessage,
    onClear,
    onSearch,
    onSelect,
    options,
    placeholder = '',
    renderDropdown,
    renderListOption,
    ...props
}: AutocompleteProps<T>): JSX.Element {
    const [inputValue, setInputValue] = React.useState(initialInputValue || '');

    const renderList = (requestClose: () => void) => (
        <Loader loading={isLoadingOptions}>
            {options.length > 0 && (
                <DefaultDropdownMenu
                    getOptionTemplate={option => <>{renderListOption(option)}</>}
                    getValue={renderListOption}
                    items={options}
                    onSelect={option => {
                        onSelect(option);
                        setInputValue(renderListOption(option));
                        requestClose();
                    }}
                />
            )}
            {!(options.length > 0) && notFoundMessage !== undefined && (
                <DropdownListItem onClick={() => false}>
                    {isString(notFoundMessage) ? <p>{notFoundMessage}</p> : notFoundMessage}
                </DropdownListItem>
            )}
        </Loader>
    );

    const updateInput = (value: string) => {
        setInputValue(value);
        onSearch(value);
    };

    return (
        <CustomDropdown
            menuStyle={{ maxHeight: '300px', overflowY: 'auto', ...menuStyle }}
            render={({ disabled, requestToggle }) => (
                <PuppetTextInput
                    clearable
                    data-cy="autocomplete-input"
                    disabled={disabled}
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={event => updateInput(event.target.value)}
                    onClear={() => {
                        updateInput('');
                        onClear?.();
                    }}
                    onClick={() => {
                        requestToggle({ show: true });
                    }}
                    onKeyDown={() => {
                        requestToggle({ show: true });
                    }}
                    {...props}
                />
            )}
        >
            {({ requestClose }) =>
                renderDropdown !== undefined ? renderDropdown(() => renderList(requestClose)) : renderList(requestClose)
            }
        </CustomDropdown>
    );
}

Autocomplete.displayName = 'Autocomplete';
